import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';

import { Customer } from '../../../customer/customer.model';
import { ShipmentDelivery } from '../../models/delivery/shipment-delivery.model';
import { ShipmentDeliveryReleaseAmendment, ShipmentReleaseAttachmentPreview, ShipmentReleaseCandidate } from '../../shipment.service';

export type ShipmentDispatchDialogCommand =
    | { type: 'close-watcher' }
    | { type: 'select-watcher'; contact?: Customer }
    | { type: 'save-watcher' }
    | { type: 'close-amendment' }
    | { type: 'set-amendment-step'; step: 1 | 2 | 3 }
    | { type: 'toggle-release'; releaseId?: number }
    | { type: 'set-release-search'; value: string }
    | { type: 'search-releases' }
    | { type: 'page-releases'; page: number }
    | { type: 'attach-releases-to-shipment' }
    | { type: 'apply-shipment-attachment' }
    | { type: 'preview-amendment' }
    | { type: 'confirm-amendment-prices' }
    | { type: 'set-amendment-buying-price'; value?: number }
    | { type: 'set-amendment-selling-price'; value?: number }
    | { type: 'set-amendment-reason'; value: string }
    | { type: 'apply-amendment' }
    | { type: 'retry-bayan'; amendment: ShipmentDeliveryReleaseAmendment }
    | { type: 'close-cancellation' }
    | { type: 'set-cancellation-reason'; value: string }
    | { type: 'confirm-cancellation' };

/**
 * Presentation-only dialogs for the Shipment Dispatch tab. All mutations are
 * emitted as typed commands and remain coordinated by the route shell.
 */
@Component({
    selector: 'app-shipment-dispatch-dialogs',
    standalone: true,
    templateUrl: './shipment-dispatch-dialogs.component.html',
    styleUrls: ['./shipment-dispatch-dialogs.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, SelectModule, TagModule]
})
export class ShipmentDispatchDialogsComponent {
    readonly watcherVisible = input(false);
    readonly watcherOptions = input<Customer[]>([]);
    readonly selectedWatcher = input<Customer | undefined>();
    readonly watcherLoading = input(false);
    readonly watcherSaving = input(false);

    readonly amendmentVisible = input(false);
    readonly shipmentReleaseMode = input(false);
    readonly shipmentNumber = input<string | undefined>();
    readonly amendmentStep = input<1 | 2 | 3>(1);
    readonly amendmentLoading = input(false);
    readonly amendmentApplying = input(false);
    readonly amendmentDelivery = input<ShipmentDelivery | undefined>();
    readonly amendmentCanApply = input(true);
    readonly releasePool = input<readonly ShipmentReleaseCandidate[]>([]);
    readonly releaseSearch = input('');
    readonly releasePage = input(0);
    readonly releaseTotalPages = input(0);
    readonly releaseTotal = input(0);
    readonly releasePoolError = input('');
    readonly selectedReleaseIds = input<readonly number[]>([]);
    readonly amendmentPreview = input<ShipmentDeliveryReleaseAmendment | undefined>();
    readonly attachmentPreview = input<ShipmentReleaseAttachmentPreview | undefined>();
    readonly zeroSellShipment = input(false);
    readonly amendmentHistory = input<readonly ShipmentDeliveryReleaseAmendment[]>([]);
    readonly amendmentReason = input('');
    readonly amendmentBuyingPrice = input<number | undefined>();
    readonly amendmentSellingPrice = input<number | undefined>();

    readonly cancellationVisible = input(false);
    readonly cancellationDelivery = input<ShipmentDelivery | undefined>();
    readonly cancellationReason = input('');
    readonly cancellationSaving = input(false);

    readonly command = output<ShipmentDispatchDialogCommand>();

    releaseSelected(releaseId?: number): boolean {
        return !!releaseId && this.selectedReleaseIds().includes(releaseId);
    }
}
